import { useCallback, useContext, useEffect, useMemo } from 'react';

import { UserContext, handleTokensEpxUpdate } from '@contexts/user-context';
import type { AxiosError, AxiosResponse } from 'axios';

import { useLogout } from '@features/submit-logout';

import { AuthEndpoints } from '../constants';
import { axiosInstance } from '../utils';

export const useAxiosInterceptors = () => {
  const { handleLogout } = useContext(UserContext);
  const { refetch: refetchLogout } = useLogout();

  const handleResponse = useCallback((response: AxiosResponse) => {
    const {
      headers: {
        'access-token-expires': accessExp,
        'refresh-token-expires': refreshExp,
      },
    } = response;
    handleTokensEpxUpdate(accessExp, refreshExp);

    return response;
  }, []);

  const handleError = useCallback(
    async (err: AxiosError) => {
      const status = err?.response?.status;
      const url = err?.config?.url;

      if ((status === 401 || status === 403) && url !== AuthEndpoints.LOGIN) {
        refetchLogout({ cancelRefetch: false });
        handleLogout();
      }

      return Promise.reject(err);
    },
    [handleLogout, refetchLogout]
  );

  const interceptor = useMemo(
    () => axiosInstance.interceptors.response.use(handleResponse, handleError),
    [handleResponse, handleError]
  );

  useEffect(() => {
    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [interceptor]);
};
